// §glp1-preview checkout terms. The weight-loss catalog carries the same
// product at several supply lengths as separate SKUs (e.g. baseProductKey
// `semaglutide-0.6mg-30d` and `semaglutide-0.6mg-90d`, the latter named
// "… (3 month)"). This resolves the monthly / 3 / 6 / 12-month siblings of a
// resolved product so the checkout can offer a term picker.
//
// Every term is backed by a catalog SKU: it is charged and submitted as that
// SKU's medId, so pricing stays catalog-driven (admin drug-catalog + the
// per-tenant TenantDrugPricing overlay via ?t=) and the BE derives the billing
// cadence from its daySupply (docs/BACKEND_SQUARE_SUBSCRIPTIONS_BILLING_PRD.md:
// 21–45→monthly, 75–100→quarterly, 165–200→every six months; 330–380→yearly
// per docs/BACKEND_PLAN_TERMS_6_12_MONTH_PRD.md). A term with no SKU in the
// catalog is reported with `available:false` and the FE never invents a price
// for it — the BE is the single source of truth for what a patient sees.
//
// Relative import so Jest can load this without the `@/` alias.
import { additiveOf } from './wlAdditives';

export const TERMS = [
  { days: 30, months: 1, label: 'Monthly Plan', supply: '4-week supply', sub: 'Billed every month', line: 'Flexible — adjust or cancel any time' },
  { days: 90, months: 3, label: '3-Month Plan', supply: '12-week supply', sub: 'Billed every 90 days', line: 'Receive your 3 month supply in a single shipment', badge: 'Most popular' },
  { days: 180, months: 6, label: '6-Month Plan', supply: '24-week supply', sub: 'Billed every 6 months', line: 'Your plan for consistency and lasting results' },
  { days: 360, months: 12, label: '12-Month Plan', supply: '48-week supply', sub: 'Billed once a year', line: 'Commit to a year of progress and save the most', badge: 'Best value' },
];

// Which term bucket a SKU's daySupply falls in (mirrors the BE cadence ranges,
// extended for a yearly SKU).
export function termDays(daySupply) {
  const d = Number(daySupply);
  if (d >= 21 && d <= 45) return 30;
  if (d >= 75 && d <= 100) return 90;
  if (d >= 165 && d <= 200) return 180;
  if (d >= 330 && d <= 380) return 360;
  return null;
}

// Product name with the supply-length suffix removed, so "Semaglutide 0.6mg"
// and "Semaglutide 0.6mg (3 month)" compare equal.
export function productName(item) {
  return String((item && item.name) || '').replace(/\s*\(\s*\d+\s*months?\s*\)\s*/gi, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
}

// Family key: baseProductKey without its `-<n>d` suffix, else name+strength.
export function familyKey(item) {
  if (!item) return '';
  const k = String(item.baseProductKey || '');
  if (k) return k.replace(/-\d+d$/i, '');
  return `${productName(item)}|${String(item.strength || '').toLowerCase()}`;
}

// For a resolved base product, one entry per TERM:
//   { ...term, available, sku, priceCents, perMonthCents, saveCents, savePct }
// available:false (sku null) when the catalog has no sibling at that length.
// Same pharmacy only (prices and fulfilment differ per pharmacy); exact
// product-name match is preferred over a family match because two 30-day SKUs
// can share a baseProductKey (e.g. "Semaglutide 0.2mg" and "Semaglutide
// 0.2mg/0.4mg").
export function termOptions(base, meds) {
  if (!base || !Array.isArray(meds)) return [];
  const fam = familyKey(base);
  const name = productName(base);
  const pool = meds.filter((m) => m && !additiveOf(m) && m.pharmacy === base.pharmacy);
  const at = (days) => (termDays(base.daySupply) === days ? base : pick(pool, days, fam, name, base));
  const monthly = at(30);
  const monthlyRef = monthly && Number(monthly.price) > 0 ? Number(monthly.price) : null;
  return TERMS.map((t) => {
    const sku = at(t.days);
    if (!sku) return { ...t, available: false, sku: null, priceCents: null, perMonthCents: null, saveCents: 0, savePct: 0 };
    const priceCents = Number(sku.price) || 0;
    const perMonthCents = Math.round(priceCents / t.months);
    const saveCents = monthlyRef && t.months > 1 ? Math.max(0, monthlyRef * t.months - priceCents) : 0;
    const savePct = monthlyRef && t.months > 1 ? Math.max(0, Math.round((1 - perMonthCents / monthlyRef) * 100)) : 0;
    return { ...t, available: true, sku, priceCents, perMonthCents, saveCents, savePct };
  });
}

function pick(pool, days, fam, name, base) {
  const atTerm = pool.filter((m) => termDays(m.daySupply) === days);
  if (!atTerm.length) return null;
  const byName = atTerm.filter((m) => productName(m) === name);
  const byFam = atTerm.filter((m) => familyKey(m) === fam);
  const c = byName.length ? byName : byFam;
  if (!c.length) return null;
  // Same strength first, then cheapest.
  return c.slice().sort((a, b) =>
    Number(String(b.strength) === String(base.strength)) - Number(String(a.strength) === String(base.strength))
    || (Number(a.price) || 0) - (Number(b.price) || 0))[0];
}

export const fmtUsd = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;
export const fmtUsdRound = (cents) => `$${Math.round((Number(cents) || 0) / 100)}`;
